import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CheckCircle, WarningCircle, Car } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const vLabel = (v) => `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() + (v.license_plate ? ` — ${v.license_plate}` : "");

export function ChauffeurImportSummary({ result, vehicles = [], onClose, onAssigned }) {
  const [rows, setRows] = useState(() => result?.unmatched_vehicles || []);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState("");

  if (!result) return null;

  const assign = async (label) => {
    const vid = picks[label];
    if (!vid) { toast.error("Pick a fleet vehicle first"); return; }
    setBusy(label);
    try {
      const { data } = await api.post("/bookings/assign-vehicle-by-label", { label, vehicle_id: vid });
      toast.success(`Assigned ${data.updated} booking(s) to ${data.vehicle_label}`);
      setRows((r) => r.filter((x) => x.label !== label));
      onAssigned && onAssigned();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="chauffeur-import-summary">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Chauffeur import complete</DialogTitle>
          <DialogDescription>Historical chauffeur bookings were added as completed trips.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Imported" value={result.imported} testid="summary-imported" />
          <Stat label="Duplicates skipped" value={result.skipped_duplicates} testid="summary-skipped" />
          <Stat label="Total value" value={money(result.total_value)} testid="summary-total" />
        </div>

        {rows.length === 0 ? (
          <div className="mt-2 flex items-center gap-2 rounded-md border border-emerald-700/40 bg-emerald-950/10 px-4 py-3 text-sm text-emerald-400" data-testid="summary-all-matched">
            <CheckCircle size={16} /> All vehicles were matched to your fleet — profit splits are set.
          </div>
        ) : (
          <div className="mt-2">
            <div className="flex items-center gap-2 text-sm text-amber-400 mb-2">
              <WarningCircle size={16} /> {rows.reduce((s, r) => s + r.count, 0)} booking(s) across {rows.length} car name(s) didn’t match a fleet vehicle. Assign them to lock in the correct split.
            </div>
            <div className="max-h-[320px] overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="unmatched-vehicle-list">
              {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`unmatched-row-${r.label}`}>
                  <div className="flex items-center gap-2 min-w-[160px]">
                    <Car size={16} className="text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">{r.label || "(blank)"}</div>
                      <div className="text-xs text-muted-foreground">{r.count} booking(s)</div>
                    </div>
                  </div>
                  <Select value={picks[r.label] || ""} onValueChange={(v) => setPicks((p) => ({ ...p, [r.label]: v }))}>
                    <SelectTrigger className="bg-input border-border h-9 flex-1 min-w-[220px]" data-testid={`assign-select-${r.label}`}><SelectValue placeholder="Match to fleet vehicle…" /></SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{vLabel(v)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => assign(r.label)} disabled={busy === r.label} data-testid={`assign-btn-${r.label}`}>
                    {busy === r.label ? "Assigning…" : "Assign"}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">You can also skip this and assign vehicles later by editing each booking.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="summary-close-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="label-mono text-xs">{label}</div>
      <div className="font-heading text-xl mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
